<template>
  <div class="container py-4" v-if="assessment">
    <div class="card shadow-lg mb-4 rounded-4 overflow-hidden">
      <!-- Header -->
      <div
        class="card-header bg-primary text-white text-center text-md-start py-3 px-4"
      >
        <h2 class="fw-bold mb-2">{{ assessment.title }}</h2>
        <div
          class="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start"
        >
          <span class="badge bg-info text-dark">Topic: {{ topicName }}</span>
          <span class="badge bg-secondary">Subject: {{ subjectName }}</span>
          <span class="badge bg-success">Questions: {{ questions.length }}</span>
        </div>
      </div>

      <!-- Body -->
      <div class="card-body bg-light">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary mb-3" role="status"></div>
          <p class="text-primary fw-semibold">Loading questions...</p>
        </div>

        <!-- Questions -->
        <div v-else-if="pages.length">
          <!-- Progress -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="fw-semibold text-primary">
              Page {{ currentPageIndex + 1 }} of {{ pages.length }}
            </span>
            <div class="progress w-50" style="height: 10px">
              <div
                class="progress-bar bg-primary"
                role="progressbar"
                :style="{ width: ((currentPageIndex + 1) / pages.length) * 100 + '%' }"
              ></div>
            </div>
          </div>

          <!-- Render parent + subs -->
          <div
            v-for="(q, qIndex) in currentPageQuestions"
            :key="q.key"
            class="mb-4"
          >
            <!-- Question Text -->
            <div class="question-text mb-3 p-3 rounded shadow-sm">
              <span class="question-number fw-bold">
                {{ questionLabel(q, qIndex) }}.
              </span>
              <span v-html="renderMath(q.question_text, q.is_math)"></span>

              <!-- If parent is a stem -->
              <div v-if="q.question_type === 'parent'" class="mt-2 text-muted small">
                Answer the sub-questions below.
              </div>
            </div>

            <!-- Question Image -->
            <div v-if="q.question_image_url" class="text-center mb-3">
              <img
                :src="q.question_image_url"
                alt="Question Image"
                class="img-fluid rounded-3 shadow-sm"
                style="max-height: 300px"
              />
            </div>

            <!-- Options / Answers (skip for parent stem) -->
            <div class="options-list mb-3" v-if="q.question_type !== 'parent'">
              <!-- MCQ (single or multiple) -->
              <template v-if="q.question_type === 'mcq'">
                <!-- Multiple answers -->
                <template v-if="q.multiple_answers">
                  <div
                    v-for="(option, i) in q.options"
                    :key="i"
                    class="mb-2"
                  >
                    <input
                      class="visually-hidden"
                      type="checkbox"
                      :id="'mcq-m-' + q.id + '-' + i"
                      :value="option.text"
                      v-model="answers[currentPageIndex][qIndex]"
                    />
                    <label
                      class="option-item p-2 bg-white rounded-3 shadow-sm d-flex align-items-start gap-3"
                      :class="{
                        active: Array.isArray(answers[currentPageIndex][qIndex]) &&
                          answers[currentPageIndex][qIndex].includes(option.text)
                      }"
                      :for="'mcq-m-' + q.id + '-' + i"
                    >
                      <span class="option-letter">{{ String.fromCharCode(65 + i) }}</span>
                      <span
                        class="option-content"
                        v-html="renderMath(option.text, q.is_math)"
                      ></span>

                      <img
                        v-if="option.image"
                        :src="option.image"
                        alt="Option Image"
                        class="img-fluid ms-2"
                        style="max-height: 100px"
                      />
                    </label>
                  </div>
                </template>

                <!-- Single answer -->
                <template v-else>
                  <div
                    v-for="(option, i) in q.options"
                    :key="i"
                    class="mb-2"
                  >
                    <input
                      class="visually-hidden"
                      type="radio"
                      :id="'mcq-s-' + q.id + '-' + i"
                      :name="'mcq-' + q.id"
                      :value="option.text"
                      v-model="answers[currentPageIndex][qIndex]"
                    />
                    <label
                      class="option-item p-2 bg-white rounded-3 shadow-sm d-flex align-items-start gap-3"
                      :class="{ active: answers[currentPageIndex][qIndex] === option.text }"
                      :for="'mcq-s-' + q.id + '-' + i"
                    >
                      <span class="option-letter">{{ String.fromCharCode(65 + i) }}</span>
                      <span
                        class="option-content"
                        v-html="renderMath(option.text, q.is_math)"
                      ></span>

                      <img
                        v-if="option.image"
                        :src="option.image"
                        alt="Option Image"
                        class="img-fluid ms-2"
                        style="max-height: 100px"
                      />
                    </label>
                  </div>
                </template>
              </template>

              <!-- True/False -->
              <div v-else-if="q.question_type === 'true_false'" class="d-flex flex-column gap-2">
                <div v-for="(opt, i) in tfOptions" :key="i" class="mb-2">
                  <input
                    class="visually-hidden"
                    type="radio"
                    :id="'tf-' + q.id + '-' + i"
                    :name="'tf-' + q.id"
                    :value="opt.value"
                    v-model="answers[currentPageIndex][qIndex]"
                  />
                  <label
                    class="option-item p-2 bg-white rounded-3 shadow-sm d-flex align-items-center gap-3 fw-semibold"
                    :class="{ active: answers[currentPageIndex][qIndex] === opt.value }"
                    :for="'tf-' + q.id + '-' + i"
                  >
                    <span class="option-letter">{{ opt.label.charAt(0) }}</span>
                    <span class="option-content">{{ opt.label }}</span>
                  </label>
                </div>
              </div>

              <!-- Short Answer -->
              <div v-else-if="q.question_type === 'short_answer'" class="mt-3">
                <textarea
                  v-model="answers[currentPageIndex][qIndex]"
                  class="form-control shadow-sm"
                  rows="3"
                  placeholder="Type your answer here..."
                ></textarea>
              </div>

              <!-- Matching -->
              <div v-else-if="q.question_type === 'matching'" class="mt-3">
                <div
                  v-for="(leftItem, i) in q.options.left"
                  :key="i"
                  class="d-flex align-items-center justify-content-between gap-2 mb-2 p-2 bg-white rounded-3 shadow-sm"
                >
                  <span class="fw-semibold">{{ leftItem }}</span>

                  <!-- store RIGHT INDEX (number) -->
                  <select
                    class="form-select w-50"
                    v-model.number="answers[currentPageIndex][qIndex][i]"
                  >
                    <option :value="-1">Select match</option>
                    <option
                      v-for="(rightOpt, j) in q.options.right"
                      :key="j"
                      :value="j"
                    >
                      {{ rightOpt }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="d-flex flex-wrap justify-content-end gap-2 mt-3">
            <button
              class="btn btn-outline-secondary fw-bold"
              @click="prevPage"
              :disabled="currentPageIndex === 0"
            >
              <i class="bi bi-arrow-left-circle me-2"></i>Previous
            </button>

            <button
              class="btn btn-outline-primary fw-bold"
              @click="nextPage"
              :disabled="currentPageIndex === pages.length - 1"
            >
              Next<i class="bi bi-arrow-right-circle ms-2"></i>
            </button>

            <button
              v-if="currentPageIndex === pages.length - 1"
              class="btn btn-success fw-bold"
              @click.prevent="submitAnswers"
              :disabled="!allPagesAnswered() || submitting"
            >
              <i class="bi bi-send-check me-2"></i>
              {{ submitting ? "Submitting..." : "Submit" }}
            </button>
          </div>
        </div>

        <!-- No Questions -->
        <div v-else class="text-center py-5 text-muted lead">
          No questions found for this assessment.
        </div>

        <!-- Message -->
        <div
          v-if="message"
          class="mt-4 text-center fw-bold"
          :class="submitSuccess ? 'text-success' : 'text-danger'"
        >
          {{ message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import "katex/dist/katex.min.css";
import { renderMath } from "@/utils/mathRenderer";

export default {
  name: "PracticeSession",

  data() {
    return {
      assessment: null,
      pages: [],
      questions: [],
      answers: [],
      currentPageIndex: 0,
      message: "",
      submitSuccess: false,
      submitting: false,
      loading: true,
      topic: null,
      studentAssessmentId: null,
      subject: null,
    };
  },

  computed: {
    topicName() {
      return this.topic?.topic_name || this.assessment?.topic_name || "N/A";
    },
    subjectName() {
      return this.subject || "N/A";
    },

    tfOptions() {
      return [
        { label: "True", value: "true" },
        { label: "False", value: "false" },
      ];
    },

    currentPageQuestions() {
      if (!this.pages.length) return [];
      const page = this.pages[this.currentPageIndex];
      return [page.parent, ...page.subs];
    },
  },

  async created() {
    const token = localStorage.getItem("auth_token");
    const id = this.$route.params.id;

    this.loading = true;
    try {
      const res = await axios.get(`/assessments/${id}/questions-for-practice`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      this.assessment = res.data.assessment || {};
      this.topic = res.data.topic || {};
      this.subject = res.data.subject || null;
      this.studentAssessmentId = this.assessment.student_assessment_id || null;

      const raw = Array.isArray(this.assessment.questions)
        ? this.assessment.questions
        : [];

      this.questions = this.flattenQuestions(raw);
      this.pages = this.groupQuestionsForWizard(raw);

      // init answers
      this.answers = this.pages.map((page) => {
        const qs = [page.parent, ...page.subs];
        return qs.map((q) => this.initAnswerForQuestion(q));
      });
    } catch (err) {
      this.message = err.response?.data?.message || "Failed to load assessment.";
    } finally {
      this.loading = false;
    }
  },

  methods: {
    // ---------------- Normalization helpers ----------------
    parseMaybeJson(val, fallback) {
      if (val == null) return fallback;
      if (typeof val !== "string") return val;
      try {
        return JSON.parse(val);
      } catch {
        return fallback;
      }
    },

    normalizeMcqOptions(options) {
      // options can be: ["A", "B"] OR [{text,image}] OR stringified
      const parsed = this.parseMaybeJson(options, []);
      const arr = Array.isArray(parsed) ? parsed : [];

      return arr.map((opt) => {
        if (opt && typeof opt === "object") {
          return {
            text: String(opt.text ?? opt.label ?? opt.value ?? ""),
            image: opt.image ?? opt.image_url ?? opt.imageUrl ?? null,
          };
        }
        return { text: String(opt ?? ""), image: null };
      });
    },

    normalizeMatchingOptions(options) {
      const parsed = this.parseMaybeJson(options, { left: [], right: [] });
      const left = Array.isArray(parsed?.left) ? parsed.left : [];
      const right = Array.isArray(parsed?.right) ? parsed.right : [];
      return { left, right };
    },

    initAnswerForQuestion(q) {
      // Parent stem never answered
      if (q.question_type === "parent") return null;

      if (q.question_type === "matching") {
        const leftLen = (q.options?.left || []).length;
        // use -1 as "unselected"
        return Array(leftLen).fill(-1);
      }

      if (q.question_type === "mcq" && q.multiple_answers) {
        return [];
      }

      // true_false / short_answer / mcq single
      return "";
    },

    questionLabel(q, qIndex) {
      // qIndex 0 is parent, 1..n are sub-questions when present
      const page = this.pages[this.currentPageIndex];
      const hasSubs = page.subs.length > 0;

      if (hasSubs) {
        if (q.is_sub) return String.fromCharCode(96 + q.sub_index + 1); // a,b,c...
        return String(this.currentPageIndex + 1); // parent stem number
      }

      // no subs: just number the page question
      return String(this.currentPageIndex + 1);
    },

    // ---------------- Data shaping from API ----------------
    flattenQuestions(rawQuestions) {
      const final = [];
      const addedParents = new Set();

      rawQuestions.forEach((aq) => {
        const qObj = aq.question || {};

        // Add parent if exists
        if (qObj.parent && !addedParents.has(qObj.parent.id)) {
          final.push(this.normalizeQuestion(qObj.parent, qObj.parent.id, false));
          addedParents.add(qObj.parent.id);
        }

        // Add current q
        const isSub = !!qObj.parent;
        const parentId = qObj.parent?.id || null;

        final.push(
          this.normalizeQuestion(qObj, aq.question_id || qObj.id, isSub, parentId)
        );
      });

      return final;
    },

    groupQuestionsForWizard(rawQuestions) {
      const pages = [];

      rawQuestions.forEach((aq) => {
        const q = aq.question;
        if (!q) return;

        // Parent question
        if (!q.parent) {
          pages.push({
            parent: this.normalizeQuestion(q, q.id, false),
            subs: [],
          });
          return;
        }

        // Sub-question
        const parent = q.parent;

        let page = pages.find((p) => p.parent.id === parent.id);
        if (!page) {
          page = {
            parent: this.normalizeQuestion(parent, parent.id, false),
            subs: [],
          };
          pages.push(page);
        }

        const subIndex = page.subs.length; // 0,1,2...
        page.subs.push(this.normalizeQuestion(q, q.id, true, parent.id, subIndex));
      });

      // if parent has subs, make parent a stem only
      pages.forEach((p) => {
        if (p.subs.length > 0) {
          p.parent.question_type = "parent";
          p.parent.options = [];
        }
      });

      return pages;
    },

    normalizeQuestion(qObj, questionId, isSub = false, parentId = null, subIndex = 0) {
      const isMath = !!qObj.is_math;

      const base = {
        key: `${questionId}-${isSub ? "sub" : "p"}`,
        id: questionId,
        question_id: questionId,
        is_sub: isSub,
        sub_index: subIndex,
        parent_id: parentId,
        is_math: isMath,
        question_image_url: qObj.question_image_url || null,
        question_text: qObj.question || "",
        multiple_answers: !!qObj.multiple_answers,
      };

      const type = qObj.question_type;

      // matching
      if (type === "matching") {
        return {
          ...base,
          question_type: "matching",
          options: this.normalizeMatchingOptions(qObj.options),
        };
      }

      // short answer
      if (type === "short_answer") {
        return {
          ...base,
          question_type: "short_answer",
          options: [],
        };
      }

      // true/false
      if (type === "true_false") {
        return {
          ...base,
          question_type: "true_false",
          options: [{ text: "True" }, { text: "False" }],
        };
      }

      // default mcq
      return {
        ...base,
        question_type: "mcq",
        options: this.normalizeMcqOptions(qObj.options),
      };
    },

    // ---------------- Wizard navigation ----------------
    prevPage() {
      if (this.currentPageIndex > 0) this.currentPageIndex--;
    },

    nextPage() {
      if (this.allAnsweredOnPage(this.currentPageIndex)) {
        if (this.currentPageIndex < this.pages.length - 1) this.currentPageIndex++;
      } else {
        const hasSubs = this.pages[this.currentPageIndex].subs.length > 0;
        this.message = hasSubs
          ? "Please answer all sub-questions on this page."
          : "Please answer the question on this page.";
        setTimeout(() => (this.message = ""), 3000);
      }
    },

    allAnsweredOnPage(pageIndex) {
      const page = this.pages[pageIndex];
      const pageAnswers = this.answers[pageIndex];
      const hasSubs = page.subs.length > 0;

      if (hasSubs) {
        // validate sub questions only (indexes 1..)
        return page.subs.every((q, i) => {
          const ans = pageAnswers[i + 1];
          if (q.question_type === "matching") {
            return Array.isArray(ans) && ans.length && ans.every((v) => v !== -1);
          }
          if (q.question_type === "mcq" && q.multiple_answers) {
            return Array.isArray(ans) && ans.length > 0;
          }
          return ans != null && String(ans).trim() !== "";
        });
      }

      // validate parent when no subs
      const q = page.parent;
      const ans = pageAnswers[0];
      if (q.question_type === "matching") {
        return Array.isArray(ans) && ans.length && ans.every((v) => v !== -1);
      }
      if (q.question_type === "mcq" && q.multiple_answers) {
        return Array.isArray(ans) && ans.length > 0;
      }
      return ans != null && String(ans).trim() !== "";
    },

    allPagesAnswered() {
      return this.pages.every((_, i) => this.allAnsweredOnPage(i));
    },

    // ---------------- Rendering ----------------
    renderMath(text, isMath = false) {
      if (!text) return "";
      return renderMath(text); // your utility already handles delimiters
    },

    // ---------------- Submit ----------------
    async submitAnswers() {
      this.submitting = true;
      this.message = "";
      const token = localStorage.getItem("auth_token");

      try {
        const answersPayload = [];

        this.pages.forEach((page, pageIdx) => {
          const hasSubs = page.subs.length > 0;

          const pushAnswer = (q, userAnswer) => {
            const type = q.question_type;

            // matching: convert right_index[] -> pairs [{left_index,right_index}]
            if (type === "matching") {
              const leftLen = (q.options?.left || []).length;
              const arr = Array.isArray(userAnswer) ? userAnswer.slice(0, leftLen) : [];
              while (arr.length < leftLen) arr.push(-1);

              const pairs = arr.map((rightIndex, leftIndex) => ({
                left_index: leftIndex,
                right_index: Number(rightIndex),
              }));

              answersPayload.push({
                question_id: q.question_id || q.id,
                answer: pairs,
              });
              return;
            }

            // true/false
            if (type === "true_false") {
              const v = userAnswer != null ? String(userAnswer).toLowerCase() : "";
              answersPayload.push({
                question_id: q.question_id || q.id,
                answer: v === "true" || v === "false" ? v : "",
              });
              return;
            }

            // short answer
            if (type === "short_answer") {
              answersPayload.push({
                question_id: q.question_id || q.id,
                answer: userAnswer != null ? String(userAnswer).trim() : "",
              });
              return;
            }

            // mcq multiple
            if (type === "mcq" && q.multiple_answers) {
              answersPayload.push({
                question_id: q.question_id || q.id,
                answer: Array.isArray(userAnswer) ? userAnswer : [],
              });
              return;
            }

            // mcq single (string)
            answersPayload.push({
              question_id: q.question_id || q.id,
              answer: userAnswer != null ? String(userAnswer) : "",
            });
          };

          if (hasSubs) {
            page.subs.forEach((q, i) => {
              const userAnswer = this.answers[pageIdx][i + 1];
              pushAnswer(q, userAnswer);
            });
          } else {
            const q = page.parent;
            const userAnswer = this.answers[pageIdx][0];
            pushAnswer(q, userAnswer);
          }
        });

        const payload = {
          student_assessment_id: this.studentAssessmentId || this.assessment.id,
          answers: answersPayload,
        };

        await axios.post("/assessments/submit-answers", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        this.submitSuccess = true;
        this.message = "Answers submitted successfully!";

        setTimeout(() => {
          this.$router.push({
            name: "StudentPracticeList",
            query: { success: "Practice submitted successfully!" },
          });
        }, 1200);
      } catch (err) {
        this.submitSuccess = false;
        this.message = err.response?.data?.message || "Submission failed. Try again.";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.question-number {
  color: #4338ca;
  margin-right: 0.5rem;
}
select,
textarea {
  border-radius: 0.6rem;
}
.option-item {
  border: 1px solid #e5e7eb;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  width: 100%;
}
.option-item:hover {
  background-color: #f8fafc;
  border-color: #d1d5db;
}
.option-item.active {
  border-color: #6366f1;
  box-shadow: 0 0 0 0.2rem rgba(99, 102, 241, 0.15);
  background-color: #eef2ff;
}
.option-letter {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background-color: #eef2ff;
  color: #4338ca;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  border: 1px solid #c7d2fe;
}
.option-item.active .option-letter {
  background-color: #c7d2fe;
  color: #1d4ed8;
  border-color: #818cf8;
}
.option-content {
  line-height: 1.4;
}
.options-list select.form-select {
  min-width: 220px;
}
</style>
